const PARAMS = {
    filter: 'filter[]',
    priceMin: 'price_min',
    priceMax: 'price_max',
    q: 'q',
    sort: 'sort',
    key: 'key'
};

const SEARCH_ROUTE_PREFIX = '/products/search';
const CATEGORY_ROUTE_RE = /^\/category\/\d+\/?$/;

const isSearchStateRoute = pathname => {
    if (!pathname) {
        return false;
    }

    return pathname.startsWith(SEARCH_ROUTE_PREFIX) || CATEGORY_ROUTE_RE.test(pathname);
};

const base64UrlDecode = value => {
    if (!value) {
        return null;
    }

    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padding = normalized.length % 4;
    const padded = padding === 0 ? normalized : normalized + '='.repeat(4 - padding);

    try {
        const binary = atob(padded);
        const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
        return new TextDecoder('utf-8').decode(bytes);
    } catch (error) {
        return null;
    }
};

const decodeFilterValue = encoded => {
    if (!encoded || encoded[0] !== 'f') {
        return null;
    }

    const delimiterPos = encoded.indexOf('_');
    if (delimiterPos <= 1) {
        return null;
    }

    const featureId = encoded.slice(1, delimiterPos);
    if (!/^[0-9]+$/.test(featureId)) {
        return null;
    }

    const valuePart = encoded.slice(delimiterPos + 1);
    if (!valuePart) {
        return null;
    }

    const decoded = base64UrlDecode(valuePart);
    if (!decoded) {
        return null;
    }

    return { featureId, value: decoded };
};

/**
 * Сформировать payload для API /products/search/state.
 * @param {URLSearchParams} formParams
 * @param {URLSearchParams} urlParams
 * @returns {{ payload: object|null, decodedCount: number }}
 */
export const buildSearchStatePayload = (formParams, urlParams) => {
    const payload = {};
    const filters = {};

    if (!formParams || !urlParams) {
        return { payload: null, decodedCount: 0 };
    }

    const queryValue = urlParams.get(PARAMS.q);
    if (queryValue && queryValue.trim() !== '') {
        payload.q = queryValue.trim();
    }

    const sortValue = urlParams.get(PARAMS.sort);
    if (sortValue && sortValue.trim() !== '') {
        payload.sort = sortValue.trim();
    }

    let decodedCount = 0;
    formParams.getAll(PARAMS.filter).forEach(value => {
        const decoded = decodeFilterValue(value);
        if (!decoded) {
            return;
        }

        decodedCount++;
        if (!filters[decoded.featureId]) {
            filters[decoded.featureId] = [];
        }

        filters[decoded.featureId].push(decoded.value);
    });

    const priceMin = formParams.get(PARAMS.priceMin);
    const priceMax = formParams.get(PARAMS.priceMax);

    if (priceMin || priceMax) {
        filters.price = {};
        if (priceMin) {
            filters.price.gte = parseFloat(priceMin);
        }
        if (priceMax) {
            filters.price.lte = parseFloat(priceMax);
        }
    }

    if (Object.keys(filters).length > 0) {
        payload.filters = filters;
    }

    if (Object.keys(payload).length === 0) {
        return { payload: null, decodedCount };
    }

    return { payload, decodedCount };
};

/**
 * Сохранить состояние поиска на сервере и вернуть ключ.
 * @param {object} payload
 * @returns {Promise<string|null>}
 */
export const postSearchState = async payload => {
    if (!payload) {
        return null;
    }

    try {
        const response = await fetch('/products/search/state', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            return null;
        }

        const data = await response.json();
        if (!data || !data.key) {
            return null;
        }

        return data.key;
    } catch (error) {
        return null;
    }
};

/**
 * Построить URL редиректа для маршрута состояния поиска.
 * @param {{ formParams: URLSearchParams, currentUrl?: URL }} config
 * @returns {Promise<string|null>}
 */
export const resolveSearchStateRedirect = async ({ formParams, currentUrl }) => {
    const url = currentUrl || new URL(window.location.href);

    if (!formParams) {
        return null;
    }

    const hasFilterState =
        formParams.has(PARAMS.filter) ||
        formParams.has(PARAMS.priceMin) ||
        formParams.has(PARAMS.priceMax);
    const isSearchRoute = isSearchStateRoute(url.pathname);

    if (!hasFilterState || !isSearchRoute) {
        return null;
    }

    const { payload, decodedCount } = buildSearchStatePayload(formParams, url.searchParams);
    if (!payload || (formParams.has(PARAMS.filter) && decodedCount === 0)) {
        return null;
    }

    const stateKey = await postSearchState(payload);
    if (!stateKey) {
        return null;
    }

    const redirectParams = new URLSearchParams(url.search);
    redirectParams.delete(PARAMS.filter);
    redirectParams.delete(PARAMS.priceMin);
    redirectParams.delete(PARAMS.priceMax);
    redirectParams.delete(PARAMS.q);
    redirectParams.delete(PARAMS.sort);
    redirectParams.delete(PARAMS.key);
    redirectParams.set(PARAMS.key, stateKey);

    return url.origin + url.pathname + '?' + redirectParams.toString();
};
